import React, { useState } from 'react';
import { Box, IconButton, Typography, TextField, Button, Select, MenuItem, FormControl } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { PaginationProps } from '../../shared/types';
import PaginationDark from "./PaginationDark";

const PaginationLight: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalCount,
  itemsPerPage,
  onItemsPerPageChange,
  showItemsPerPageSelect = true,
}) => {
  const [goToPage, setGoToPage] = useState<string>('');

  const handleGoToPage = () => {
    const pageNumber = parseInt(goToPage);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setGoToPage('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleGoToPage();
    }
  };

  const getPages = (current: number, total: number): (number | "...")[] => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, 5, "...", total];
    if (current >= total - 2) return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "...", current - 1, current, current + 1, "...", total];
  };

  const visiblePages = getPages(currentPage, totalPages);

  // Hiç veri yoksa pagination'ı gizle
  if (!totalCount || totalCount === 0) {
    return null;
  }

  // Sayfa bilgilerini hesapla
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount || 0);

  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
      {/* Ana pagination container */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'end', 
        alignItems: 'center', 
        width: '100%', 
        py: 1,
        flexWrap: 'wrap',
        gap: "30px"
      }}>

           {/* Orta - Sayfa atlama */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              sx={{
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '20px',
                color: '#172B4D',
              }}
            >
              Sayfa
            </Typography>
            <TextField
              size="small"
              type="number"
              value={goToPage}
              onChange={(e) => setGoToPage(e.target.value)}
              onKeyPress={handleKeyPress}
              inputProps={{
                min: 1,
                max: totalPages,
                style: {
                  padding: '4px 8px',
                  fontSize: '14px',
                  textAlign: 'center',
                  width: '50px',
                  height: '20px'
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '28px',
                  borderRadius: '3px',
                  background: '#FFFFFF',
                  '& fieldset': {
                    borderColor: '#DFE1E6',
                  },
                  '&:hover fieldset': {
                    borderColor: '#42526E',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0052CC',
                  },
                },
              }}
            />
            <Typography sx={{ fontSize: 14, color: '#42526E' }}>/ {totalPages}</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleGoToPage}
              disabled={!goToPage || parseInt(goToPage) < 1 || parseInt(goToPage) > totalPages}
              sx={{
                minWidth: '50px',
                height: '28px',
                textTransform: 'none',
                borderRadius: '3px',
                borderColor: '#DFE1E6',
                '&:hover': {
                  borderColor: '#42526E',
                  backgroundColor: '#F4F5F7'
                },
                            fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '20px',
                color: '#172B4D',
              }}
            >
              Git
            </Button>
          </Box>
        )}
        

     {/* Sağ taraf - Sayfa navigasyonu */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", height: 32, gap: 0.5 }}>
            {/* Önceki sayfa */}
            <Box
              role="button"
              tabIndex={0}
              onClick={() => currentPage > 1 && onPageChange(Math.max(1, currentPage - 1))}
              sx={{
                     width: 33,
                height: 36,
                border: "1px solid #DFE1E6",
                borderRadius: "3px",
                display: "grid",
                placeItems: "center",
                cursor: currentPage > 1 ? "pointer" : "not-allowed",
                opacity: currentPage > 1 ? 1 : 0.5,
                '&:hover': {
                  backgroundColor: currentPage > 1 ? '#F4F5F7' : '#FFFFFF'
                }
              }}
            >
              <ArrowDropDown style={{ transform: "rotate(90deg)", color: "#42526E" }} />
            </Box>
            
            {/* Sayfa numaraları */}
            {visiblePages.map((page, idx) =>
              page === "..." ? (
                <Box key={`dots-${idx}`} sx={{ 
                  width: 33,
                  height: 36,
                  cursor:"pointer",
                  background: "#FFFFFF",
                  border: "1px solid #EBECF0",
                  borderRadius: "3px", 
                  display: "grid", 
                  placeItems: "center", 
                  px: 1,
                }}>
                  <Box component="span" sx={{ color: "#42526E", fontSize: 14, fontFamily: "inherit" }}>…</Box>
                </Box>
              ) : (
                <Box
                  key={`p-${page}`}
                  onClick={() => page !== currentPage ? onPageChange(Number(page)) : null}
                  sx={{
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "8px 12px",
                      width: 33,
                      height: 36,
                      background:  page === currentPage ? "#172B4D" : "#FFFFFF", 
                      border: "1px solid #EBECF0",
                      borderRadius: "4px",
                      cursor:"pointer",
                  }}
                >
                  <Box component="span" sx={{ 
                    color: page === currentPage ? "#FFFFFF" : "#172B4D", 
                    fontSize: 14, 
                    fontFamily: "inherit",
                    fontWeight: page === currentPage ? 600 : 400
                  }}>
                    {page}
                  </Box>
                </Box>
              )
            )}
            
            {/* Sonraki sayfa */}
            <Box
              role="button"
              tabIndex={0}
              onClick={() => currentPage < totalPages && onPageChange(Math.min(totalPages, currentPage + 1))}
              sx={{
                width: 33,
                height: 36,
                border: "1px solid #EBECF0",
                borderRadius: "3px",
                display: "grid",
                placeItems: "center",
                cursor: currentPage < totalPages ? "pointer" : "not-allowed",
                opacity: currentPage < totalPages ? 1 : 0.5,
                '&:hover': {
                  backgroundColor: currentPage < totalPages ? '#F4F5F7' : '#FFFFFF'
                }
              }}
            >
              <ArrowDropDown style={{ transform: "rotate(-90deg)", color: "#42526E" }} />
            </Box>
          </Box>
        )}

        {/* Sol taraf - Toplam sayı bilgisi ve sayfa başına veri seçimi */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {showItemsPerPageSelect && onItemsPerPageChange && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ 
                            fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '20px',
              color: '#172B4D',
              }}>
                Sayfa başına gösterilen sonuç
              </Typography>
              <FormControl size="small">
                <Select
                  value={itemsPerPage}
                  onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                  sx={{
                    height: '28px',
                    fontSize: '14px',
                    minWidth: '60px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '3px',
                    },
                    '& fieldset': {
                      borderColor: '#DFE1E6',
                    },
                    '&:hover fieldset': {
                      borderColor: '#42526E',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0052CC',
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
                  <Typography 
            variant="body2" 
            sx={{ 
                   fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '20px',
              color: '#172B4D',
            }}
          >
            {startItem}-{endItem} / {totalCount} 
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const Pagination: React.FC<PaginationProps> = (props) =>
  props.darkModeActiveProp ? <PaginationDark {...props} /> : <PaginationLight {...props} />;

export default Pagination;